import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored user data on mount
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      let parsed;
      try {
        parsed = JSON.parse(userData);
      } catch (e) {
        console.error('Failed to parse userData from localStorage:', e);
        localStorage.removeItem('userData');
        localStorage.removeItem('token');
        setLoading(false);
        return;
      }
      // Normalize userId for consistency
      if (!parsed.userId && parsed._id) {
        parsed.userId = parsed._id;
      }
      setUser(parsed);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setLoading(false);
  }, []);

  const login = async (identifier, password) => {
    try {
      // Send identifier (username or email) instead of just email
      const response = await axios.post('/api/auth/login', { 
        identifier: identifier.trim(), 
        password 
      });

      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('userData', JSON.stringify(response.data.data));

        setUser({
          userId: response.data.data.userId,
          username: response.data.data.username,
          email: response.data.data.email
        });

        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.token}`;
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Provide helpful error messages based on backend responses
      const errorMsg = error.response?.data?.message;
      
      if (errorMsg && errorMsg.includes('required')) {
        return { 
          success: false, 
          message: 'Please enter your username/email and password' 
        };
      } else if (errorMsg && errorMsg.includes('credentials')) {
        return { 
          success: false, 
          message: 'Invalid username or email. Please check and try again.' 
        };
      } else if (errorMsg) {
        return { success: false, message: errorMsg };
      } else {
        return { 
          success: false, 
          message: 'Login failed. Please check your credentials and try again.' 
        };
      }
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post('/api/auth/register', { username, email, password });

      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('userData', JSON.stringify(response.data.data));

        setUser({
          userId: response.data.data.userId,
          username: response.data.data.username,
          email: response.data.data.email
        });

        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.token}`;
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Provide helpful error messages based on backend responses
      const errorMsg = error.response?.data?.message;
      
      if (errorMsg && (errorMsg.includes('username') || errorMsg.includes('email'))) {
        return { 
          success: false, 
          message: 'This username or email is already taken. Please choose a different one.' 
        };
      } else if (errorMsg) {
        return { success: false, message: errorMsg };
      } else {
        return { 
          success: false, 
          message: 'Registration failed. Please try again.' 
        };
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        localStorage.setItem('userData', JSON.stringify(response.data.data));
        setUser(response.data.data);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
